import useUrlState from '@ahooksjs/use-url-state'
import { useRequest, useTitle } from 'ahooks'
import { Button, Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteChildrenProps, useHistory } from 'react-router-dom'

import { hasPointPagePermission, LockIcon, NoPermissionPage, PERMISSIONS } from '@/components'
import { PpFileContainer } from '@/components/files'
import { ReportContainer } from '@/components/reports/report.container'
import { ResourceAudit } from '@/components/resource-audit'
import { BigProjectResourceDashboard } from '@/components/user-resource-dashboard/big-project/big-project-resource-dashboard'
import { SpecialProjectWorkResourceContainer } from '@/components/work-resource/special-project/special-project-work-resource.container'
import { Header } from '@/modules/header'
import { userInfoAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, ResourceType, ResourceUser } from '@/schemas'
import { useAxiosWithAtom } from '@/utils'
import { ga, traceEvent } from '@/utils/ga'

import { customerAdminAtoms, financialOperatorAtoms } from '../customer.atom'
import { Customer, OverseaRegion } from '../customer.schema'
import { FlowStatusButton } from '../flow-status/flow-status-button.component'
import { CustomerHeaderContainer } from './customer-header/customer-header.container'
import { CustomerJQLContainer } from './customer-jql/customer-jql.container'
import { CustomerSummaryContainer } from './customer-summary'

const { TabPane } = Tabs

export const CustomerDetailContainer: React.FC<RouteChildrenProps<{ selfId: string }>> = ({ match }) => {
  const { t } = useTranslation()

  const ref = React.useRef<{
    workResourceRefresh?: () => void
    auditRefresh?: () => void
  }>()

  const selfId = match!.params.selfId
  const reportRef = React.useRef<{
    entryTime: number
    name?: string
    entry?: string
    users?: ResourceUser[]
  }>({
    entryTime: +new Date(),
    users: [],
  })

  const { data: customer, refresh } = useRequest<Customer>(
    {
      url: `/api/customer/detail/${selfId}`,
    },
    {
      onError() {
        history.push('/customer')
      },
    }
  )

  const { data: financialOperators } = useAxiosWithAtom(financialOperatorAtoms, {
    url: '/api/customer/financial-operators',
  })

  const { data: admins } = useAxiosWithAtom(customerAdminAtoms, {
    url: '/api/customer/admins',
  })

  const customerAdmins = React.useMemo(() => {
    const isOversea = customer?.region === OverseaRegion
    return isOversea ? admins.oversea : admins.china
  }, [admins, customer])
  const customerFinancialOperators = React.useMemo(() => {
    const isOversea = customer?.region === OverseaRegion
    return isOversea ? financialOperators.oversea : financialOperators.china
  }, [financialOperators, customer])

  useTitle(customer ? customer.name : document.title, {
    restoreOnUnmount: true,
  })

  const [users, setUsers] = React.useState<ResourceUser[]>()
  const [isShowingHistory, setIsShowingHistory] = React.useState(false)
  React.useEffect(() => {
    if (users) {
      // reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])
  const [viewer] = useAtom(userInfoAtom)

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report.name && report.entry && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_customer_detail, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: report.entry,
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_customer_detail,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === viewer.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    const entry = window.sessionStorage.getItem('entry')
    reportRef.current = assign(reportRef.current, {
      entry: entry || 'ef_pmo_customers',
    })
    sessionStorage.removeItem('entry')

    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      window.removeEventListener('beforeunload', reportExposeTime)
      reportExposeTime()
    }
  }, [])

  React.useEffect(() => {
    if (customer) {
      reportRef.current = assign(reportRef.current, { name: customer.name })
    }
  }, [customer])

  const history = useHistory()
  const [state, setState] = useUrlState(
    { tab: new URLSearchParams(window.location.search).get('tab') || 'detail' },
    {
      navigateMode: 'replace',
    }
  )

  if (!customer) {
    return (
      <div className='flex h-full justify-center items-center'>
        <Spin size='large' delay={300} />
      </div>
    )
  }

  const onTabTrace = (tab: string, customerName: string) => {
    const customerDetail = traceEvent.customerDetail
    switch (tab) {
      case 'detail':
        ga('event', customerDetail.events.click_switch_tab, {
          event_category: customerDetail.event_categories.ef_pmo_customers_detail,
          action: customerDetail.action,
          event_label: customerName,
          target: customerDetail.targets.detail,
        })
        break
      case 'report':
        ga('event', customerDetail.events.click_switch_tab, {
          event_category: customerDetail.event_categories.ef_pmo_customers_detail,
          action: customerDetail.action,
          event_label: customerName,
          target: customerDetail.targets.weekly_reports,
        })
        break
    }
  }

  return (
    <>
      <Header
        title={<CustomerHeaderContainer project={customer} refreshProject={refresh} />}
        actions={[
          <div className='ml-5 flex gap-2' key='flow'>
            <FlowStatusButton
              customer={customer}
              key={'flow'}
              buttonType={'primary'}
              refresh={() => {
                refresh()
                ref.current?.workResourceRefresh?.()
                ref.current?.auditRefresh?.()
              }}
              admins={customerAdmins}
              financialOperators={customerFinancialOperators}
            />
          </div>,
        ]}
      />
      <Tabs
        destroyInactiveTabPane
        activeKey={state.tab}
        onChange={(tab) => {
          const keys = Object.keys(state)
          setState({
            ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
            tab,
          })
          onTabTrace(tab, customer.name)
          if (tab === 'detail') refresh()
        }}
        tabBarExtraContent={
          <Button type='link' onClick={() => setIsShowingHistory(!isShowingHistory)}>
            {isShowingHistory ? t('hide history') : t('show history')}
          </Button>
        }
      >
        <TabPane
          tab={
            <span>
              {t('overview')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='detail'
        >
          {state.tab === 'detail' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, customer.permissions) ? (
              <CustomerSummaryContainer
                customer={customer}
                setUsers={setUsers}
                refresh={refresh}
                showRightPanel={isShowingHistory}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.概况} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('requirement problem list')}
              {!hasPointPagePermission(
                PAGE_RESOURCE_POINTS_ENUM.需求与问题,
                PERMISSIONS.READ,
                customer.permissions
              ) && <LockIcon />}
            </span>
          }
          key='list'
        >
          {state.tab === 'list' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.需求与问题, PERMISSIONS.READ, customer.permissions) ? (
              <CustomerJQLContainer customer={customer} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.需求与问题} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('resource dashboard')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='dashboard'
        >
          {state.tab === 'dashboard' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, customer.permissions) ? (
              <BigProjectResourceDashboard
                selfId={customer.selfId}
                resourceType={ResourceType.CUSTOMER}
                resource={customer}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源看板} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('customer.resource')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='userResource'
        >
          {state.tab === 'userResource' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, customer.permissions) ? (
              <SpecialProjectWorkResourceContainer ref={ref} resource={customer} resourceType={ResourceType.CUSTOMER} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源管理} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('reports')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='report'
        >
          {state.tab === 'report' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, customer.permissions) ? (
              <ReportContainer
                selfId={customer.selfId}
                type={customer.type}
                permissions={customer.permissions}
                name={customer.name}
                refreshProject={refresh}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.周报} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('file')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='file'
        >
          {state.tab === 'file' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, customer.permissions) ? (
              <PpFileContainer pp={customer} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.文件} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('audit.title')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, customer.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='audit'
        >
          {state.tab === 'audit' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, customer.permissions) ? (
              <ResourceAudit ref={ref} selfId={customer.selfId} type={customer.type} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.审批} />
            ))}
        </TabPane>

        {/* {[CustomerFlowStatus.项目已结项].includes(customer.flowStatus) ? (
          <TabPane
            tab={
              <span>
                {t('customer.evaluation')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, customer.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='finish'
          >
            {state.tab === 'finish' &&
              (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, customer.permissions) ? (
                <ResourceEvaluationContainer onRefresh={refresh} resource={customer} />
              ) : (
                <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.结项} />
              ))}
          </TabPane>
        ) : null} */}
      </Tabs>
    </>
  )
}
